import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, Heart, Home, Calendar as CalIcon, Coins, Leaf, Sparkles,
  RefreshCw, Compass, Gem, Hash, Sun as SunIcon, Scale, Share2
} from "lucide-react";
import { Button } from "./ui/button";
import { AskWheel } from "./AskWheel";
import { ShareModal } from "./ShareModal";

const CATEGORY_META = {
  career:            { label: "Career & Calling",    icon: Briefcase, span: "md:col-span-4 md:row-span-2" },
  love:              { label: "Love & Romance",      icon: Heart,     span: "md:col-span-2 md:row-span-2" },
  family:            { label: "Family & Home",       icon: Home,      span: "md:col-span-2 md:row-span-2" },
  future:            { label: "The Year Ahead",      icon: CalIcon,   span: "md:col-span-4 md:row-span-2" },
  wealth:            { label: "Wealth & Finance",    icon: Coins,     span: "md:col-span-3" },
  health:            { label: "Health & Vitality",   icon: Leaf,      span: "md:col-span-3" },
  strengths_shadows: { label: "Strengths & Shadows", icon: Scale,     span: "md:col-span-6" },
};

const ORDER = ["career", "love", "family", "future", "wealth", "health", "strengths_shadows"];

const LuckyTile = ({ label, value, Icon }) => (
  <div className="flex flex-col items-center text-center px-2 sm:px-3 py-3 sm:py-4 rounded-xl bg-white/[0.025] border border-white/10 min-w-0">
    <Icon className="h-4 w-4 text-stardust mb-1.5 sm:mb-2" />
    <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">{label}</p>
    <p className="text-white text-[12px] sm:text-sm font-medium leading-snug break-words w-full">{value}</p>
  </div>
);

export const Results = ({ data, onRestart, readOnly = false }) => {
  const [shareOpen, setShareOpen] = useState(false);
  const [thread, setThread] = useState(() => {
    // Hydrate from data.questions if a shared reading is being viewed
    return Array.isArray(data?.questions) ? data.questions : [];
  });
  if (!data) return null;

  const context = {
    name: data.name,
    date_of_birth: data._meta?.date_of_birth,
    time_of_birth: data._meta?.time_of_birth || null,
    place_of_birth: data._meta?.place_of_birth || "",
    zodiac_sign: data.zodiac_sign,
    cosmic_profile: data.cosmic_profile || "",
    predictions: data.predictions || null,
    lucky: data.lucky || null,
  };

  const showAsk = !readOnly || thread.length > 0;

  return (
    <section
      data-testid="results-section"
      className="relative min-h-screen px-4 sm:px-6 pt-24 sm:pt-28 pb-20 max-w-6xl mx-auto"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center mb-12 sm:mb-16 flex flex-col items-center"
      >
        <p className="text-stardust/70 uppercase tracking-[0.4em] sm:tracking-[0.5em] text-[10px] mb-5 sm:mb-6">An almanac for</p>
        <h2
          data-testid="results-name"
          className="font-display text-4xl sm:text-6xl text-[#F5EBD8] mb-8 sm:mb-10 break-words"
        >
          {data.name}<span className="text-stardust">.</span>
        </h2>

        <div className="zodiac-orb mb-6 animate-float-slow" data-testid="results-zodiac-orb">
          <span style={{ fontFamily: "'DM Serif Display', serif" }}>
            {data.zodiac_symbol + "\uFE0E"}
          </span>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 flex-wrap justify-center max-w-xs sm:max-w-none">
          <span className="text-stardust uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[11px] sm:text-xs" data-testid="results-zodiac">
            {data.zodiac_sign}
          </span>
          <span className="text-white/20">·</span>
          <span className="text-slate-400 uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[9px] sm:text-[10px]">{data.element}</span>
          <span className="text-white/20">·</span>
          <span className="text-slate-400 uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[9px] sm:text-[10px]">{data.quality}</span>
          <span className="text-white/20 hidden sm:inline">·</span>
          <span className="text-slate-400 uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[9px] sm:text-[10px] w-full sm:w-auto sm:inline">Ruled by {data.ruling_planet}</span>
        </div>

        <p
          data-testid="results-cosmic-profile"
          className="font-serif-italic text-slate-300 text-base sm:text-2xl max-w-3xl leading-relaxed px-2"
        >
          {data.cosmic_profile}
        </p>
      </motion.div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5 auto-rows-[minmax(160px,auto)]">
        {ORDER.map((key, idx) => {
          const pred = data.predictions?.[key];
          if (!pred) return null;
          const meta = CATEGORY_META[key];
          const Icon = meta.icon;
          return (
            <motion.article
              key={key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08 * idx }}
              className={`bento-card p-5 sm:p-7 flex flex-col ${meta.span}`}
              data-testid={`result-${key}-card`}
            >
              <div className="flex items-center gap-3 mb-3 sm:mb-4">
                <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-stardust/10 border border-stardust/30 flex items-center justify-center shrink-0">
                  <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-stardust" />
                </div>
                <span className="text-stardust/80 uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[9px] sm:text-[10px]">{meta.label}</span>
              </div>

              <h3 className="font-display text-xl sm:text-3xl text-[#F5EBD8] mb-3 leading-tight">
                {pred.title}
              </h3>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4 sm:mb-5">
                {pred.summary}
              </p>

              {pred.insights?.length > 0 && (
                <ul className="space-y-2 mt-auto">
                  {pred.insights.map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-400 text-[13px] sm:text-sm leading-relaxed">
                      <span className="text-stardust mt-0.5 sm:mt-1 shrink-0">✦</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.article>
          );
        })}
      </div>

      {/* Lucky forecast */}
      {data.lucky && (
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          data-testid="lucky-forecast"
          className="mt-12 sm:mt-16"
        >
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-stardust/70 uppercase tracking-[0.4em] sm:tracking-[0.5em] text-[10px] mb-3">A favourable forecast</p>
            <h3 className="font-display text-2xl sm:text-4xl text-[#F5EBD8]">
              Your fortunate signs<span className="text-stardust">.</span>
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            <LuckyTile label="Day" value={data.lucky.lucky_day} Icon={SunIcon} />
            <LuckyTile
              label="Colours"
              value={(data.lucky.lucky_colors || (data.lucky.lucky_color ? [data.lucky.lucky_color] : [])).join(" · ") || "·"}
              Icon={Sparkles}
            />
            <LuckyTile
              label="Numbers"
              value={(data.lucky.lucky_numbers || (data.lucky.lucky_number ? [data.lucky.lucky_number] : [])).join(" · ") || "·"}
              Icon={Hash}
            />
            <LuckyTile
              label="Gemstones"
              value={(data.lucky.lucky_gemstones || (data.lucky.lucky_gemstone ? [data.lucky.lucky_gemstone] : [])).join(" · ") || "·"}
              Icon={Gem}
            />
            <LuckyTile label="Direction" value={data.lucky.lucky_direction} Icon={Compass} />
            <LuckyTile
              label="Allies"
              value={(data.lucky.compatible_signs || []).slice(0, 3).join(" · ") || "·"}
              Icon={Heart}
            />
          </div>

          {data.lucky.power_mantra && (
            <p
              data-testid="power-mantra"
              className="text-center font-serif-italic text-base sm:text-2xl text-stardust-light mt-8 sm:mt-10 px-4"
            >
              &ldquo;{data.lucky.power_mantra}&rdquo;
            </p>
          )}
        </motion.section>
      )}

      {/* Ask the wheel */}
      {showAsk && (
        <AskWheel
          context={context}
          thread={thread}
          onThreadChange={setThread}
          readOnly={readOnly}
        />
      )}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.9 }}
        className="flex flex-col items-center mt-20 sm:mt-24 gap-4"
      >
        <Sparkles className="h-5 w-5 text-stardust mb-2 animate-twinkle" />
        {!readOnly ? (
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Button
              data-testid="results-share-button"
              onClick={() => setShareOpen(true)}
              className="bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-8 py-6 font-medium shadow-[0_0_28px_rgba(226,194,117,0.25)] hover:shadow-[0_0_44px_rgba(226,194,117,0.45)] transition-all duration-300"
            >
              <Share2 className="h-4 w-4 mr-3" />
              Share my almanac
            </Button>
            <Button
              data-testid="results-restart-button"
              onClick={onRestart}
              variant="outline"
              className="border-stardust/40 text-stardust hover:bg-stardust hover:text-cosmic-900 hover:border-stardust rounded-md uppercase tracking-[0.3em] text-xs px-8 py-6 transition-all duration-300"
            >
              <RefreshCw className="h-4 w-4 mr-3" />
              New reading
            </Button>
          </div>
        ) : (
          <Link
            to="/"
            data-testid="shared-cta-button"
            className="inline-flex items-center bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-10 py-5 font-medium shadow-[0_0_30px_rgba(226,194,117,0.3)] transition-all duration-300"
          >
            <Sparkles className="h-4 w-4 mr-3" />
            Get my own reading
          </Link>
        )}
        <p className="text-slate-500 text-xs uppercase tracking-[0.25em] mt-4 text-center px-6">
          For reflection, not prescription · <Link to="/legal" className="hover:text-stardust transition-colors">Privacy &amp; legal</Link>
        </p>
      </motion.div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} reading={data} thread={thread} />
    </section>
  );
};
